import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { notification as antdNotification } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { useAuth } from "./AuthContext";

// Notification sound - simple beep
const NOTIFICATION_SOUND_URL =
  "data:audio/wav;base64,UklGRl9vT19teleQYAQKPd7qJlAQBBqODxnF0AAEKs4/OYVwAARK/l9JRRAQBGsuf1kEsBAEi06/aNRQEASrbt+IlAAQBMuO/5hTsBAE667vmBNgEAULzw+n0xAQBSvvH7eSsBAFS/8vt1JgEAVsHz/HEhAQBYwvT8bRwBAFrD9f1pFwEAXMT2/WUSAQBexff9YQ0BAF/G+P5dCAEAYcf5/lkDAQBjyPr+VQABAGTJe/5RAQEAZsp8/k0BAQBoy33+SQEBAGnMfv5FAQEAa81//kEBAQBszn/+PQEBAGzPgP45AQEAbdCA/jUBAQBu0YH+MQEBAHDSgv4tAQEAcdOC/ikBAQBy1IP+JQEBAHPVhP4hAQEAdNaE/h0BAQBz1oT+GQEBAHPWhP4VAQEAc9aE/hEBAQBz1oT+DQEBAHPWhP4JAQEAc9aE/gUBAQBz1oT+AQEBAHPWBP4AAQEA";

const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Ignore - user may not have interacted with page yet
    });
  } catch {
    // Ignore audio errors
  }
};

export interface ChatNotification {
  id: string;
  purchaseOrderId: number;
  poNumber: string;
  senderName: string;
  messagePreview: string;
  timestamp: Date;
}

interface POChatNotificationContextType {
  // Unread message counts per order
  unreadCounts: Map<string, number>;
  getUnreadCount: (orderId: string) => number;
  clearUnreadCount: (orderId: string) => void;
  clearAllUnreadCounts: () => void;
  // Latest notification for display
  latestNotification: ChatNotification | null;
  // Connection status
  isConnected: boolean;
}

const POChatNotificationContext = createContext<POChatNotificationContextType | undefined>(undefined);

const WS_PATH = "/ws/po-notifications";
const RECONNECT_DELAY = 5000;
const PING_INTERVAL = 30000;

const buildWebSocketUrl = (token: string): string | null => {
  try {
    const apiUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      console.error("[POChatNotificationContext] No API URL configured");
      return null;
    }

    const url = new URL(apiUrl);
    url.protocol = "ws:";
    url.pathname = WS_PATH;
    url.search = new URLSearchParams({ token }).toString();
    
    const wsUrl = url.toString();
    return wsUrl;
  } catch (error) {
    console.error("[POChatNotificationContext] Failed to build WebSocket URL:", error);
    return null;
  }
};

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("accessToken");
};

interface WebSocketEvent {
  type: "po.chat.notification" | "po.notifications.connected" | "pong";
  data?: {
    purchaseOrderId?: number;
    poNumber?: string;
    senderUserId?: string;
    senderName?: string;
    messagePreview?: string;
    timestamp?: string;
    userId?: string;
  };
}

export const POChatNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map());
  const [latestNotification, setLatestNotification] = useState<ChatNotification | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  const getUnreadCount = useCallback(
    (orderId: string) => unreadCounts.get(orderId) || 0,
    [unreadCounts]
  );

  const clearUnreadCount = useCallback((orderId: string) => {
    setUnreadCounts((prev) => {
      const next = new Map(prev);
      next.delete(orderId);
      return next;
    });
  }, []);

  const clearAllUnreadCounts = useCallback(() => {
    setUnreadCounts(new Map());
  }, []);

  const handleNotification = useCallback(
    (data: WebSocketEvent["data"]) => {
      if (!data?.purchaseOrderId || !data?.poNumber || !data?.senderName) return;
      
      if (data.senderUserId === user?.id) return;

      const orderIdStr = String(data.purchaseOrderId);

      setUnreadCounts((prev) => {
        const next = new Map(prev);
        next.set(orderIdStr, (prev.get(orderIdStr) || 0) + 1);
        return next;
      });

      const notification: ChatNotification = {
        id: `${data.purchaseOrderId}-${Date.now()}`,
        purchaseOrderId: data.purchaseOrderId,
        poNumber: data.poNumber,
        senderName: data.senderName,
        messagePreview: data.messagePreview || "",
        timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
      };

      setLatestNotification(notification);

      // Play sound
      playNotificationSound();

      // Show toast
      antdNotification.info({
        message: `New message on ${data.poNumber}`,
        description: (
          <div>
            <strong>{data.senderName}</strong>: {data.messagePreview || "sent a message"}
          </div>
        ),
        icon: <MessageOutlined style={{ color: "#1890ff" }} />,
        placement: "topRight",
        duration: 6,
        key: `po-chat-${data.purchaseOrderId}-${Date.now()}`,
      });

      // Clear latest notification after delay
      setTimeout(() => {
        setLatestNotification((current) =>
          current?.id === notification.id ? null : current
        );
      }, 10000);
    },
    [user?.id]
  );

  const connect = useCallback(() => {
    if (!isAuthenticated || !mountedRef.current) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    const wsUrl = buildWebSocketUrl(token);
    if (!wsUrl) {
      return;
    }

    // Close existing connection
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    
    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

    socket.onopen = () => {
      if (!mountedRef.current) return;
      setIsConnected(true);

      pingIntervalRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, PING_INTERVAL);
    };

    socket.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const payload = JSON.parse(event.data) as WebSocketEvent;
        if (payload.type === "po.chat.notification" && payload.data) {
          handleNotification(payload.data);
        }
      } catch (error) {
        console.error("[POChatNotificationContext] Failed to parse message:", error);
      }
    };

    socket.onclose = (event) => {
      if (!mountedRef.current) return;
      setIsConnected(false);
      socketRef.current = null;

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Reconnect if still authenticated
      if (isAuthenticated && mountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
      }
    };

    socket.onerror = (error) => {
      console.error("[POChatNotificationContext] WebSocket error:", error);
      console.error("[POChatNotificationContext] Failed URL was:", wsUrl);
      // Error will trigger close event
    };
    
    } catch (error) {
      console.error("[POChatNotificationContext] Failed to create WebSocket:", error);
      setIsConnected(false);
      socketRef.current = null;
    }
  }, [isAuthenticated, handleNotification]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect when authenticated
  useEffect(() => {
    mountedRef.current = true;
    

    if (isAuthenticated && user?.id) {
      // Small delay to ensure token is available
      const timeoutId = setTimeout(() => {
        connect();
      }, 500);
      
      return () => {
        clearTimeout(timeoutId);
        mountedRef.current = false;
        disconnect();
      };
    } else {
      disconnect();
      setUnreadCounts(new Map());
      return () => {
        mountedRef.current = false;
      };
    }
  }, [isAuthenticated, user?.id, connect, disconnect]);

  const contextValue = useMemo<POChatNotificationContextType>(
    () => ({
      unreadCounts,
      getUnreadCount,
      clearUnreadCount,
      clearAllUnreadCounts,
      latestNotification,
      isConnected,
    }),
    [unreadCounts, getUnreadCount, clearUnreadCount, clearAllUnreadCounts, latestNotification, isConnected]
  );

  return (
    <POChatNotificationContext.Provider value={contextValue}>
      {children}
    </POChatNotificationContext.Provider>
  );
};

export const usePOChatNotifications = (): POChatNotificationContextType => {
  const context = useContext(POChatNotificationContext);
  if (!context) {
    throw new Error("usePOChatNotifications must be used within POChatNotificationProvider");
  }
  return context;
};
